import React from "react";
import { Link, useLocation } from "react-router-dom";
import { Content } from "../../components/Layout";
import { ColumnTitle } from "../../components/ColumnTitle";
import { ActionText, Box, Flex } from "../../components/Flex";
import { Scrollbar } from "../../components/Scrollbar";
import { Table } from "../../components/AntdTable";
import { Label, Paragraph, Title } from "../../components/Text";
import { EditDetailsIcon } from "../../icons/EditDetailsIcon";
import { EditIcon } from "../../icons/EditIcon";
import { PlusIcon } from "../../icons/PlusIcon";
import { formatDate } from "../../utils";
import { RemoveIcon } from "../../icons/RemoveIcon";

const OrgDetails = ({ data, showForm, loading, editContracts, deleteContract }) => {
    const path = useLocation().pathname;

    const fields = [
        { label: "organisation name", attr: "name" },
        { label: "organisation alias", attr: "alias" },
        { label: "phone number", attr: "phone" },
        { label: "contact email", attr: "email", render: d => <a href={`mailto: ${d}`}>{d}</a> },
        { label: "address", attr: "addr" },
        { label: "notes", attr: "notes" }
    ];

    const columns = [
        {
            title: ({ sortOrder, sortColumn }) => ColumnTitle(sortOrder, sortColumn, "contractName", "Architecture"),
            dataIndex: 'contractName',
            width: 112,
            sorter: (a, b) => { if (a && a.hasOwnProperty("contractName") && a.contractName) return a.contractName.localeCompare(b.contractName) }
        },
        {
            title: ({ sortOrder, sortColumn }) => ColumnTitle(sortOrder, sortColumn, "endDate", "Expiry"),
            dataIndex: "endDate",
            width: 100,
            defaultSortOrder: "descend",
            render: (text, record, index) => (formatDate(text, "DD-MMM-YYYY")),
            sorter: (a, b) => { if (a && a.hasOwnProperty("endDate") && a.endDate) return a.endDate.localeCompare(b.endDate) },
        },
        {
            dataIndex: "edit",
            width: 50,
            render: (text, record, index) => (
                <EditIcon
                    color="var(--sp-label-color)"
                    hoverColor="var(--sp-paragraph-color)"
                    onClick={() => editContracts({ alias: data.alias, name: record.contractName, endDate: record.endDate })}
                />
            )
        },
        {
            dataIndex: "remove",
            width: 50,
            render: (text, record, index) => (
                <RemoveIcon
                    small
                    color="var(--sp-label-color)"
                    hoverColor="var(--sp-paragraph-color)"
                    onClick={() => deleteContract({ alias: data.alias, contract: record.contractName, org: data.name })}
                />
            )
        }
    ];

    return(
        <Content gap={{ bottom: 28 }}>
            <Title h3 isLoading={loading}>{data && data.hasOwnProperty("name") && data.name}</Title>
            <Flex height="calc(100% - 60px)" gap={{ right: 32 }}>
                <Box flex={1} gap={{ bottom: 17 }}>
                    <Flex justify="space-between" align="center">
                        <Title h4 capitalize>about organisation</Title>
                        <ActionText onClick={() => showForm("organisations", data)}>
                            <span><EditDetailsIcon /></span>
                            <span>Edit Info</span>
                        </ActionText>
                    </Flex>
                    <Scrollbar>
                        <Flex direction="column" gap={{ bottom: 20 }}>
                            {fields.map((field, idx) => (
                                <Flex key={`field-${idx}`} direction="column">
                                    <Label>{field.label}</Label>
                                    <Paragraph isLoading={loading}>
                                        {data && data.hasOwnProperty(field.attr) && data[field.attr]
                                            ? field.render
                                                ? field.render(data[field.attr])
                                                : data[field.attr]
                                            : "-"
                                        }
                                    </Paragraph>
                                </Flex>
                            ))}
                        </Flex>
                    </Scrollbar>
                </Box>
                <Flex flex={1} style={{ maxWidth: "500px" }} direction="column" gap={{ bottom: 12 }}>
                    <Box style={{ maxHeight: "calc(100% - 168px)" }}>
                        <Flex justify="space-between">
                            <Title h4 capitalize>contracts</Title>
                            <ActionText onClick={() => showForm("contracts")}>
                                <PlusIcon />
                                <span>Add Contract</span>
                            </ActionText>
                        </Flex>
                        <div style={{ height: "calc(100% - 28px)" }}>
                            <Table
                                columns={columns}
                                dataSource={data && data.hasOwnProperty("contracts") ? data.contracts : []}
                                pagination={false}
                                rowKey="contractName"
                                scroll={{ y: true }}
                                showSorterTooltip={false}
                                locale={{ emptyText: <Label size="18px" bold color="#D7E3F7">no contracts available</Label> }}
                                loading={loading}
                            />
                        </div>
                    </Box>
                    <Box>
                        <Flex justify="space-between" align="center">
                            <Flex align="baseline" gap={{ right: 8 }}>
                                <Title h4 capitalize>
                                    <Link to={`${path}/users`}>
                                        users
                                    </Link>
                                </Title>
                            </Flex>
                            <ActionText onClick={() => showForm("users")}>
                                    <span><PlusIcon /></span>
                                <span>Add User</span>
                            </ActionText>
                        </Flex>
                    </Box>
                    <Box>
                        <Flex align="baseline" gap={{ right: 8 }}>
                            <Title h4 capitalize>
                                <Link to={`${path}/issues`}>
                                    issues
                                </Link>
                            </Title>
                        </Flex>
                    </Box>
                </Flex>
            </Flex>
        </Content>
    );
};

export { OrgDetails }